import { Link } from "react-router-dom";
import { ArrowLeft, Eye, Trash2, Shield, Ban } from "lucide-react";
import Footer from "../components/Footer";

export default function CaliforniaPrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-[#00B894] transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <img src="/images/gigseasy-logo.svg" alt="GigsEasy" className="h-10 mb-6" />
          <h1 className="text-4xl font-bold text-slate-900 mb-2">California Privacy Notice</h1>
          <p className="text-slate-500">Last Updated: March 20, 2026</p>
          <p className="text-slate-500">Effective Date: March 20, 2026</p>
        </div>

        {/* Rights at a Glance */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Eye className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Right to Know</p>
            <p className="text-xs text-slate-500">Request what data we collect</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Trash2 className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Right to Delete</p>
            <p className="text-xs text-slate-500">Request deletion of your data</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Shield className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Right to Non-Discrimination</p>
            <p className="text-xs text-slate-500">Equal service regardless of choices</p>
          </div>
          <div className="bg-slate-50 p-4 rounded-lg text-center">
            <Ban className="w-8 h-8 text-[#00B894] mx-auto mb-2" />
            <p className="font-semibold text-slate-900 text-sm">Right to Opt-Out</p>
            <p className="text-xs text-slate-500">We do not sell your data</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none">
          <p className="text-slate-600 mb-8">
            This California Privacy Notice supplements the information contained in our{" "}
            <Link to="/privacy-policy" className="text-[#00B894] hover:underline">Privacy Policy</Link> and applies 
            solely to visitors, users, and others who reside in the State of California ("consumers" or "you"). 
            GigsEasy.com is a service owned and operated by <strong>Active Consumers LLC</strong>. We adopt this notice 
            to comply with the California Consumer Privacy Act of 2018 ("CCPA") as amended by the California Privacy 
            Rights Act of 2020 ("CPRA"), and any terms defined in the CCPA/CPRA have the same meaning when used in 
            this notice.
          </p>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Information We Collect</h2>
            <p className="text-slate-600 mb-4">
              GigsEasy.com collects information that identifies, relates to, describes, references, is reasonably 
              capable of being associated with, or could reasonably be linked, directly or indirectly, with a 
              particular consumer, household, or device ("personal information"). In particular, we have collected 
              the following categories of personal information from consumers within the last twelve (12) months:
            </p>

            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-slate-200 rounded-lg text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Category</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Examples</th>
                    <th className="px-4 py-3 text-left font-semibold text-slate-900">Collected</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">A. Identifiers</td>
                    <td className="px-4 py-3 text-slate-600">Real name, email address, unique personal identifier, online identifier, IP address, account name</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">B. Personal Information (Cal. Civ. Code § 1798.80(e))</td>
                    <td className="px-4 py-3 text-slate-600">Name, address, telephone number</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">C. Protected Classification Characteristics</td>
                    <td className="px-4 py-3 text-slate-600">Age, race, color, ancestry, national origin, religion, marital status, sex, gender, sexual orientation, veteran status</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-red-600">NO</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">D. Commercial Information</td>
                    <td className="px-4 py-3 text-slate-600">Records of tasks completed, points earned, withdrawals made</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">E. Biometric Information</td>
                    <td className="px-4 py-3 text-slate-600">Genetic, physiological, behavioral characteristics, fingerprints, faceprints, voiceprints</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-red-600">NO</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">F. Internet or Network Activity</td>
                    <td className="px-4 py-3 text-slate-600">Browsing history, information on interaction with our website or application</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">G. Geolocation Data</td>
                    <td className="px-4 py-3 text-slate-600">Physical location (city/country level from IP address)</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">H. Sensory Data</td>
                    <td className="px-4 py-3 text-slate-600">Audio, electronic, visual, thermal, olfactory, or similar information</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-red-600">NO</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">I. Professional or Employment Information</td>
                    <td className="px-4 py-3 text-slate-600">Current or past job history, performance evaluations</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-red-600">NO</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">J. Education Information</td>
                    <td className="px-4 py-3 text-slate-600">Education records, student records</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-red-600">NO</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-slate-600 font-medium">K. Inferences</td>
                    <td className="px-4 py-3 text-slate-600">Profile reflecting preferences, characteristics, behavior based on collected information</td>
                    <td className="px-4 py-3 text-slate-600 font-semibold text-green-600">YES</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Sources of Personal Information</h2>
            <p className="text-slate-600 mb-4">We obtain the categories of personal information listed above from the following sources:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Directly from you:</strong> Information you provide when creating an account, completing tasks, updating preferences, or contacting support</li>
              <li><strong>Indirectly from you:</strong> Information collected automatically through your interaction with our Service, such as usage data and device information</li>
              <li><strong>From third parties:</strong> Information from service providers such as Google (for OAuth authentication), PayPal (for payment processing), and fraud prevention services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. Use of Personal Information</h2>
            <p className="text-slate-600 mb-4">We may use or disclose the personal information we collect for one or more of the following business purposes:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>To fulfill or meet the reason you provided the information (e.g., to send task notifications, process payments)</li>
              <li>To provide, support, personalize, and develop our Service</li>
              <li>To create, maintain, customize, and secure your account</li>
              <li>To process your requests, transactions, and payments</li>
              <li>To provide customer support and respond to your inquiries</li>
              <li>To personalize your experience and deliver relevant content</li>
              <li>To maintain the safety, security, and integrity of our Service</li>
              <li>For testing, research, analysis, and product development</li>
              <li>To respond to law enforcement requests and comply with applicable law</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Sharing Personal Information</h2>
            <p className="text-slate-600 mb-4">We may share your personal information with the following categories of third parties:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li><strong>Service Providers:</strong> Companies that perform services on our behalf, such as payment processing (PayPal), email delivery (Postmark), and cloud hosting (MongoDB Atlas)</li>
              <li><strong>Business Partners:</strong> Third parties with whom we partner to offer products or services</li>
              <li><strong>Affiliates:</strong> Our parent company, subsidiaries, and affiliates</li>
              <li><strong>Legal and Safety:</strong> Law enforcement, government agencies, or other parties when required by law or to protect our rights</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Sale of Personal Information</h2>
            <div className="bg-green-50 border border-green-200 p-4 rounded-lg mb-4">
              <p className="text-green-800 font-semibold">GigsEasy.com does not sell personal information to third parties.</p>
            </div>
            <p className="text-slate-600">
              In the preceding twelve (12) months, we have not sold any personal information. We do not have actual 
              knowledge that we sell the personal information of consumers under 16 years of age.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Your Rights Under the CCPA/CPRA</h2>
            <p className="text-slate-600 mb-4">
              The CCPA/CPRA provides California residents with specific rights regarding their personal information:
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.1 Right to Know and Data Portability</h3>
            <p className="text-slate-600 mb-4">
              You have the right to request that we disclose certain information to you about our collection and use of 
              your personal information over the past 12 months. Once we receive and verify your request, we will disclose:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>The categories of personal information we collected about you</li>
              <li>The categories of sources for the personal information we collected</li>
              <li>Our business or commercial purpose for collecting that personal information</li>
              <li>The categories of third parties with whom we share that personal information</li>
              <li>The specific pieces of personal information we collected about you (data portability request)</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.2 Right to Delete</h3>
            <p className="text-slate-600 mb-4">
              You have the right to request that we delete any of your personal information that we collected from you 
              and retained, subject to certain exceptions. We may deny your deletion request if retaining the information is necessary for us to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-4">
              <li>Complete the transaction for which we collected the personal information</li>
              <li>Provide a good or service that you requested</li>
              <li>Detect security incidents, protect against malicious, deceptive, fraudulent, or illegal activity</li>
              <li>Debug products to identify and repair errors</li>
              <li>Comply with a legal obligation</li>
            </ul>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.3 Right to Correct</h3>
            <p className="text-slate-600 mb-4">
              You have the right to request that we correct inaccurate personal information that we maintain about you. 
              Upon receiving and verifying your request, we will use commercially reasonable efforts to correct the inaccurate personal information.
            </p>

            <h3 className="text-xl font-semibold text-slate-800 mb-3">6.4 Right to Non-Discrimination</h3>
            <p className="text-slate-600 mb-4">
              We will not discriminate against you for exercising any of your CCPA/CPRA rights. Unless permitted by the CCPA/CPRA, we will not:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Deny you goods or services</li>
              <li>Charge you different prices or rates for goods or services</li>
              <li>Provide you a different level or quality of goods or services</li>
              <li>Suggest that you may receive a different price or rate or a different level or quality of goods or services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Exercising Your Rights</h2>
            <p className="text-slate-600 mb-4">To exercise the rights described above, please submit a verifiable consumer request to us by:</p>
            <div className="bg-slate-50 p-6 rounded-lg mb-4">
              <p className="text-slate-700"><strong>Email:</strong> support@gigseasy.com</p>
              <p className="text-slate-700"><strong>Subject Line:</strong> "California Privacy Rights Request"</p>
              <p className="text-slate-700 mt-2"><strong>Mail:</strong> Active Consumers LLC, San Juan, Puerto Rico 00901</p>
            </div>
            <p className="text-slate-600 mb-4">
              Only you, or a person you have authorized to act on your behalf, may make a verifiable consumer request 
              related to your personal information. You may only make a verifiable consumer request for access or data 
              portability twice within a 12-month period. The verifiable consumer request must:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide sufficient information that allows us to reasonably verify you are the person about whom we collected personal information or an authorized representative</li>
              <li>Describe your request with sufficient detail that allows us to properly understand, evaluate, and respond to it</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Verification Process</h2>
            <p className="text-slate-600 mb-4">
              We cannot respond to your request or provide you with personal information if we cannot verify your identity 
              or authority to make the request and confirm the personal information relates to you. For verification, we may ask you to:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Confirm the email address associated with your account</li>
              <li>Provide information that matches information we have on file</li>
              <li>Answer security questions based on your account activity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Response Timing and Format</h2>
            <p className="text-slate-600 mb-4">
              We endeavor to respond to a verifiable consumer request within forty-five (45) days of its receipt. If we 
              require more time (up to an additional 45 days), we will inform you of the reason and extension period in writing.
            </p>
            <p className="text-slate-600 mb-4">
              If you have an account with us, we will deliver our written response to that account. Any disclosures we 
              provide will only cover the 12-month period preceding the verifiable consumer request's receipt.
            </p>
            <p className="text-slate-600">
              We do not charge a fee to process or respond to your verifiable consumer request unless it is excessive, 
              repetitive, or manifestly unfounded.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Authorized Agents</h2>
            <p className="text-slate-600 mb-4">You may designate an authorized agent to make a request under the CCPA on your behalf. To do so, you must:</p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2">
              <li>Provide the authorized agent with written permission to submit the request</li>
              <li>Verify your own identity directly with us</li>
              <li>Directly confirm with us that you provided the authorized agent permission to submit the request</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. "Do Not Track" Signals</h2>
            <p className="text-slate-600">
              California law requires us to disclose how we respond to web browser "Do Not Track" signals. We do not 
              currently respond to "Do Not Track" signals because a uniform technological standard has not yet been 
              developed. We continue to review new technologies and may adopt a standard once one is created.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. "Shine the Light" Law</h2>
            <p className="text-slate-600">
              California Civil Code Section 1798.83, also known as the "Shine The Light" law, permits users who are 
              California residents to request and obtain from us, once a year and free of charge, information about 
              categories of personal information (if any) we disclosed to third parties for direct marketing purposes. 
              As stated above, we do not share personal information with third parties for their direct marketing purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Minors Under 16 Years of Age</h2>
            <p className="text-slate-600">
              Our Service is not intended for anyone under 18 years of age. We do not knowingly collect personal 
              information from minors under 16. We do not sell the personal information of consumers we know to be 
              less than 16 years of age. If you are under 16, please do not use our Service or provide any information to us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">14. Changes to This Notice</h2>
            <p className="text-slate-600">
              We reserve the right to amend this California Privacy Notice at our discretion and at any time. When we 
              make changes to this notice, we will post the updated notice on our website and update the notice's 
              effective date. Your continued use of our Service following the posting of changes constitutes your 
              acceptance of such changes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">15. Contact Information</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions or comments about this notice, our Privacy Policy, or wish to exercise your 
              rights under California law, please contact us at:
            </p>
            <div className="bg-slate-50 p-6 rounded-lg">
              <p className="font-semibold text-slate-900">Active Consumers LLC</p>
              <p className="text-slate-600">San Juan, Puerto Rico 00901</p>
              <p className="text-slate-600">Email: support@gigseasy.com</p>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
